/**
 * Utility functions for storage management and quota handling.
 * Uses IndexedDB for isolated storage.
 */

import { getIndexedDBUsage, MAX_STORAGE_BYTES } from './indexedDBStorage';

export interface StorageUsage {
  used: number;
  total: number;
  percentage: number;
}

/**
 * Get current IndexedDB storage usage for chat data.
 * Returns used bytes, total limit (5MB), and percentage.
 */
export async function getStorageUsage(): Promise<StorageUsage> {
  try {
    return await getIndexedDBUsage();
  } catch {
    // In case of any errors, return empty usage
    return { used: 0, total: MAX_STORAGE_BYTES, percentage: 0 };
  }
}

/**
 * Detect if an error is a storage-related error.
 * For IndexedDB, we check for QuotaExceededError and other storage errors.
 */
export function isStorageError(e: unknown): boolean {
  if (!(e instanceof DOMException)) {
    return false;
  }

  return (
    // Standard quota exceeded
    e.name === 'QuotaExceededError' ||
    // Firefox
    e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    // Legacy Chrome/Safari (code 22)
    e.code === 22 ||
    // Firefox legacy
    e.code === 1014
  );
}

/**
 * Get the storage limit in bytes (5MB).
 */
export function getStorageLimit(): number {
  return MAX_STORAGE_BYTES;
}
